import type { AlgoliaCompanyRecord } from '../../types/company-search-record';
import { getFirstCompanyUrl } from './first-company-url';

/**
 * Vike serializes pageContext into HTML JSON with escaped slashes; depending on how values are
 * re-hydrated, some strings can retain literal `\\/` sequences instead of `/`. SSR uses the raw
 * Algolia record, so href/src can mismatch on hydrate. Collapse those sequences for URL-like fields.
 */
export function normalizeEscapesInUrlString(value: string): string {
  let s = value;
  let prev = '';
  while (s !== prev) {
    prev = s;
    s = s.replace(/\\\//g, '/');
  }
  return s;
}

const URL_STRING_FIELDS: Array<keyof AlgoliaCompanyRecord> = [
  'companyURL',
  'logoUrl',
  'heroImageUrl',
  'linkedinUrl',
  'facebookUrl',
  'instagramUrl',
  'tiktokUrl',
  'youtubeUrl',
  'video',
  'sameAs',
];

/** Shallow copy of `company` with URL-like string fields normalized for DOM href/src. */
export function normalizeCompanyRecordUrlStrings(company: AlgoliaCompanyRecord): AlgoliaCompanyRecord {
  let changed = false;
  const out: AlgoliaCompanyRecord = { ...company };
  for (const key of URL_STRING_FIELDS) {
    const v = out[key];
    if (typeof v !== 'string') continue;

    if (key === 'companyURL') {
      const collapsed = normalizeEscapesInUrlString(v);
      const first = getFirstCompanyUrl(collapsed);
      const next = first ?? undefined;
      if (next !== v) {
        (out as Record<string, unknown>)[key] = next;
        changed = true;
      }
      continue;
    }

    if (v.includes('\\/')) {
      (out as Record<string, unknown>)[key] = normalizeEscapesInUrlString(v);
      changed = true;
    }
  }
  return changed ? out : company;
}
